import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { API_URL } from "../config/api";
import {
  MessageSquare, Plus, Loader2, Mail, MailX, X, Send,
  Smartphone, Globe, Inbox, Trash2,
} from "lucide-react";

const SOURCE_META = {
  myeuropacific: { label: "Web",     Icon: Globe,      cls: "bg-slate-100 text-slate-700" },
  outlook:       { label: "Outlook", Icon: Inbox,      cls: "bg-blue-100 text-blue-700" },
  whatsapp:      { label: "WhatsApp",Icon: Smartphone, cls: "bg-emerald-100 text-emerald-700" },
  trinet:        { label: "TriNet",  Icon: Globe,      cls: "bg-violet-100 text-violet-700" },
};

const _fmt = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch (_) {
    return iso;
  }
};

/**
 * PFComments — free-text status log for a Position Folder.
 * Renders below the milestone tracker. Creating a comment optionally fires
 * a short status-alert email to the PF customer.
 */
export function PFComments({ pfId, pfNumber, containers = [], onNotify }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    text: "",
    container_number: "",
    notify_customer: true,
    audience: "customer",  // "customer" (sub PF) | "master" (co-loader)
  });

  const containerNos = (containers || [])
    .map((c) => (c?.container_number || c || "").toString().trim().toUpperCase())
    .filter(Boolean);

  const token = localStorage.getItem("token");
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
  }), [token]);

  const fetchComments = useCallback(async () => {
    if (!pfId) return;
    try {
      setLoading(true);
      const r = await axios.get(
        `${API_URL}/position-folders/${pfId}/comments`,
        { headers: authHeaders() });
      setItems(r.data?.comments || []);
    } catch (_) {
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [pfId, authHeaders]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const openDialog = () => {
    setForm({ text: "", container_number: "", notify_customer: true, audience: "customer" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    const text = (form.text || "").trim();
    if (text.length < 3) {
      toast.error("Comment text is required (min 3 chars)");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(
        `${API_URL}/position-folders/${pfId}/comments`,
        {
          text,
          container_number: form.container_number || null,
          source: "myeuropacific",
          notify_customer: !!form.notify_customer,
          visible_to_customer: form.audience !== "master",
          audience: form.audience || "customer",
        },
        { headers: authHeaders() }
      );
      const alert = r.data?.alert || {};
      const aud_label = form.audience === "master" ? "co-loader" : "customer";
      if (form.notify_customer && alert.sent) {
        toast.success(`Comment added — ${aud_label} alert sent to ${alert.to || "notify list"}`);
      } else if (form.notify_customer && !alert.sent && alert.error === "no_recipients") {
        toast.warning(`Comment added — no ${aud_label} notification emails on PF (alert skipped)`);
      } else {
        toast.success("Comment added");
      }
      setShowDialog(false);
      fetchComments();
      if (onNotify) onNotify();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add comment");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(
        `${API_URL}/position-folders/${pfId}/comments/${commentId}`,
        { headers: authHeaders() });
      toast.success("Comment deleted");
      fetchComments();
    } catch (_) {
      toast.error("Failed to delete comment");
    }
  };

  return (
    <div
      className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden mt-4"
      data-testid="pf-comments-section"
    >
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white p-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Status Comments
          </h2>
          <p className="text-slate-300 text-sm">
            Free-text status log · {items.length} {items.length === 1 ? "entry" : "entries"} on {pfNumber}
          </p>
        </div>
        <Button
          size="sm"
          onClick={openDialog}
          className="bg-white text-slate-800 hover:bg-slate-100"
          data-testid="pf-comments-add-btn"
        >
          <Plus className="h-3 w-3 mr-1" /> Add comment
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-500" />
          <p className="text-sm text-slate-500 mt-2">Loading comments...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">No comments yet</p>
          <p className="text-sm text-slate-400">
            Use the "Add comment" button to log a status note. Visible to the customer on the tracking page.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="pf-comments-list">
          {items.map((c) => {
            const src = SOURCE_META[c.source] || SOURCE_META.myeuropacific;
            const SrcIcon = src.Icon;
            return (
              <li
                key={c.id}
                className="px-4 py-3 hover:bg-slate-50 transition-colors"
                data-testid={`pf-comment-${c.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center">
                      <MessageSquare className="h-3.5 w-3.5 text-slate-500" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-xs text-slate-500 font-mono">
                        {_fmt(c.created_at)}
                      </span>
                      <span className="text-xs font-medium text-slate-700">
                        {c.author_name || c.author_email}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${src.cls}`}>
                        <SrcIcon className="h-3 w-3" /> {src.label}
                      </span>
                      {c.container_number ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-mono">
                          {c.container_number}
                        </span>
                      ) : null}
                      {(c.audience === "master") ? (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200"
                          title="Internal — only shown on WhatsApp via comments+"
                        >
                          🔒 co-loader
                        </span>
                      ) : (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700"
                          title="Visible on tracking page + default WhatsApp track reply"
                        >
                          👤 customer
                        </span>
                      )}
                      {c.visible_to_customer === false ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">
                          internal
                        </span>
                      ) : null}
                    </div>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                      {c.text}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(c.id)}
                    className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500"
                    title="Delete comment"
                    data-testid={`pf-comment-delete-${c.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-slate-700" />
              Add comment to {pfNumber}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Comment *</Label>
              <Textarea
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                rows={4}
                placeholder="e.g. Customer asked about delivery 27.4 — checking with carrier, still waiting on POD"
                maxLength={2000}
                data-testid="pf-comments-textarea"
              />
              <p className="text-xs text-slate-400 mt-1">
                {form.text.length}/2000 — visible to the customer on the tracking page.
              </p>
            </div>

            {containerNos.length > 1 && (
              <div>
                <Label>Container (optional)</Label>
                <select
                  value={form.container_number}
                  onChange={(e) => setForm({ ...form, container_number: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500"
                  data-testid="pf-comments-container-select"
                >
                  <option value="">All containers</option>
                  {containerNos.map((cn) => (
                    <option key={cn} value={cn}>{cn}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Audience selector — drives WhatsApp visibility + which Notify list is emailed */}
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <Label className="text-xs font-medium text-slate-700">
                This comment is for…
              </Label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, audience: "customer" })}
                  className={`text-xs px-3 py-2 rounded-md border text-left transition-colors ${
                    form.audience === "customer"
                      ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                      : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"
                  }`}
                  data-testid="pf-comments-audience-customer"
                >
                  <div className="font-semibold">👤 Sub PF · Customer</div>
                  <div className={`text-[11px] mt-0.5 ${
                    form.audience === "customer" ? "text-blue-50" : "text-slate-500"
                  }`}>
                    Visible on tracking page · Email to customer Notify list · Default WhatsApp <code>track</code> shows it.
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, audience: "master" })}
                  className={`text-xs px-3 py-2 rounded-md border text-left transition-colors ${
                    form.audience === "master"
                      ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                      : "bg-white text-slate-700 border-slate-300 hover:border-amber-400"
                  }`}
                  data-testid="pf-comments-audience-master"
                >
                  <div className="font-semibold">🔒 Master PF · Co-loader</div>
                  <div className={`text-[11px] mt-0.5 ${
                    form.audience === "master" ? "text-amber-50" : "text-slate-500"
                  }`}>
                    Internal · Email to master-PF Notify list · Only surfaces on WhatsApp via <code>comments+</code>.
                  </div>
                </button>
              </div>
            </div>

            <label className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.notify_customer}
                onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })}
                className="mt-0.5"
                data-testid="pf-comments-notify-toggle"
              />
              <div className="text-sm">
                <div className="font-medium flex items-center gap-1">
                  {form.notify_customer ? (
                    <Mail className="h-3.5 w-3.5 text-emerald-700" />
                  ) : (
                    <MailX className="h-3.5 w-3.5 text-slate-400" />
                  )}
                  Send short status alert email
                </div>
                <p className="text-xs text-slate-600">
                  Goes to the <b>{form.audience === "master" ? "master-PF / co-loader" : "customer"}</b> Notify list with this comment as a one-paragraph update + tracking link.
                </p>
              </div>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="pf-comments-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Add comment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PFComments;
